import React, { useState } from "react";
import { MagicWand, FileArrowUp, PencilSimpleLine, FileText } from "@phosphor-icons/react";
import { FileLog } from "shared/types/file";
import ContractDocumentAnalyzer from "./ContractDocumentAnalyzer";
import { ExtractionContext, PreviewDocument } from "./contractExtractionApi";
import { ExtractedContractDto } from "./types";

type MethodChoice = "upload" | "existing" | null;

interface ContractWizardMethodStepProps {
  ctx: ExtractionContext;
  session: any;
  /** Called once Ella has read an uploaded contract (parent advances to the form). */
  onExtracted: (data: ExtractedContractDto) => void;
  /** Called right after upload with the stored document (for the PDF preview). */
  onDocumentReady?: (doc: PreviewDocument) => void;
  /** Already-stored Huurcontract documents for the selected unit. */
  huurcontractFileLogs: FileLog[];
  isLoadingFileLogs: boolean;
  analyzingFileLogId: number | null;
  /** Analyse an existing Huurcontract (parent advances to the form on success). */
  onAnalyzeExisting: (fileLogId: number) => void;
  /** Continue to the empty form to fill the contract in manually. */
  onManual: () => void;
  /** No unit selected yet (upload/analyse need one). */
  disabled: boolean;
  disabledReason?: string;
}

interface MethodCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const MethodCard: React.FC<MethodCardProps> = ({
  icon,
  title,
  description,
  selected,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex h-full w-full flex-col items-center gap-2 rounded-lg border p-4 text-center transition-colors ${
      disabled
        ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
        : selected
          ? "border-brick-primary bg-brick-secondary-fine"
          : "border-neutral-primary/20 bg-white hover:border-brick-primary/50"
    }`}
  >
    <div className="text-brick-primary">{icon}</div>
    <p className="text-sm font-medium text-neutral-primary">{title}</p>
    <p className="text-xs text-neutral-secondary-mid">{description}</p>
  </button>
);

/**
 * First step of the add-contract wizard: pick how to start — upload a contract
 * for Ella to read, analyse an already-uploaded Huurcontract, or fill it in
 * manually. Upload/analyse prefill the form; all three land on the form step.
 */
const ContractWizardMethodStep: React.FC<ContractWizardMethodStepProps> = ({
  ctx,
  session,
  onExtracted,
  onDocumentReady,
  huurcontractFileLogs,
  isLoadingFileLogs,
  analyzingFileLogId,
  onAnalyzeExisting,
  onManual,
  disabled,
  disabledReason,
}) => {
  const [choice, setChoice] = useState<MethodChoice>(null);

  return (
    <div className="space-y-4">
      <h3 className="border-b border-neutral-primary/20 pb-2 text-lg font-semibold text-neutral-primary">
        Hoe wil je dit contract aanmaken?
      </h3>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MethodCard
          icon={<FileArrowUp size={28} />}
          title="Uploaden & uitlezen"
          description="Sleep of upload het contract. Ella vult prijs, datums, regime en contacten automatisch in."
          selected={choice === "upload"}
          disabled={disabled}
          onClick={() => setChoice("upload")}
        />

        <MethodCard
          icon={<MagicWand size={28} weight="fill" />}
          title="Bestaand analyseren"
          description={
            disabled
              ? "Selecteer eerst een unit hierboven."
              : huurcontractFileLogs.length > 0
                ? `${huurcontractFileLogs.length} bestaand huurcontract gevonden.`
                : "Geen bestaande huurcontracten gevonden."
          }
          selected={choice === "existing"}
          disabled={disabled || huurcontractFileLogs.length === 0}
          onClick={() => setChoice("existing")}
        />

        <MethodCard
          icon={<PencilSimpleLine size={28} />}
          title="Handmatig aanmaken"
          description="Vul alle contractgegevens zelf in zonder automatische uitlezing."
          selected={false}
          onClick={onManual}
        />
      </div>

      {disabled && disabledReason && (
        <p className="text-xs font-medium text-amber-700">{disabledReason}</p>
      )}

      {/* Upload zone */}
      {choice === "upload" && !disabled && (
        <ContractDocumentAnalyzer
          ctx={ctx}
          session={session}
          onExtracted={onExtracted}
          onDocumentReady={onDocumentReady}
        />
      )}

      {/* Existing Huurcontract picker */}
      {choice === "existing" && !disabled && (
        <div className="space-y-2 rounded-lg border border-neutral-primary/20 p-3">
          {isLoadingFileLogs && (
            <p className="text-sm text-neutral-secondary-mid">Documenten laden…</p>
          )}
          {!isLoadingFileLogs && huurcontractFileLogs.length === 0 && (
            <p className="text-sm text-neutral-secondary-mid">
              Geen bestaande huurcontracten gevonden.
            </p>
          )}
          {huurcontractFileLogs.map((fileLog) => (
            <div
              key={fileLog.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-neutral-primary/20 bg-brick-secondary-fine p-3"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileText size={18} className="shrink-0 text-brick-primary" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-neutral-primary">
                    {Array.isArray(fileLog.label) ? fileLog.label.join(", ") : fileLog.label}
                  </p>
                  {fileLog.createdAt && (
                    <p className="text-xs text-neutral-secondary-mid">
                      {new Date(fileLog.createdAt).toLocaleDateString("nl-BE")}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onAnalyzeExisting(fileLog.id!)}
                disabled={analyzingFileLogId !== null || !fileLog.sharePointFileUrl}
                className="shrink-0 rounded-md bg-brick-primary px-3 py-1.5 text-xs font-medium text-white transition-opacity disabled:opacity-50"
              >
                {analyzingFileLogId === fileLog.id ? "Ella leest…" : "Analyseer met Ella"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractWizardMethodStep;
